import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClient, generatePortalToken, updateClient, deleteClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

/* Below 640px the related-record tables collapse to labelled stacked rows. */
const ROW = 'block py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', company: '', phone: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const fetchClient = () => {
    getClient(id).then(res => { setClient(res.data); setLoading(false); }).catch(() => { setLoading(false); navigate('/clients'); });
  };

  useEffect(() => { fetchClient(); }, [id, navigate]);

  const openEditDialog = () => {
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      company: client.company || '',
      phone: client.phone || '',
      address: client.address || '',
      notes: client.notes || '',
    });
    setEmailError('');
    setShowEdit(true);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleUpdate = async () => {
    if (!editForm.name.trim()) { toast.error('Client name is required'); return; }
    if (editForm.email.trim() && !isValidEmail(editForm.email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setSaving(true);
    try {
      const res = await updateClient(id, editForm);
      setClient(prev => ({ ...prev, ...res.data }));
      toast.success('Client updated');
      setShowEdit(false);
    } catch { toast.error('Failed to update client'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(id);
      toast.success('Client deleted');
      navigate('/clients');
    } catch { toast.error('Failed to delete client'); }
    setDeleting(false);
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-wash" />;
  if (!client) return null;

  const handleCopyPortalLink = async () => {
    try {
      const res = await generatePortalToken(id);
      const link = `${window.location.origin}/portal/${res.data.token}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Client portal link copied');
    } catch { toast.error('Failed to generate portal link'); }
  };

  return (
    <div data-testid="client-detail-page">
      <button
        onClick={() => navigate('/clients')}
        className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
        data-testid="back-to-clients"
      >
        <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Clients
      </button>

      <PageHeader
        title={client.name}
        description={client.company}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={openEditDialog} data-testid="edit-client-btn">Edit</Button>
            <Button variant="outline" size="sm" onClick={handleCopyPortalLink} data-testid="copy-portal-link-btn">Copy portal link</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="border-destructive/30 text-destructive hover:bg-destructive/10"
              data-testid="delete-client-btn"
            >
              Delete
            </Button>
          </>
        }
      />

      <section>
        <SectionRule label="Contact" />
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
          <div>
            <dt className="text-micro uppercase text-ink-subtle">Email</dt>
            <dd className="mt-1 text-body text-ink">{client.email || '—'}</dd>
          </div>
          <div>
            <dt className="text-micro uppercase text-ink-subtle">Phone</dt>
            <dd className="mt-1 text-body text-ink tabular-nums">{client.phone || '—'}</dd>
          </div>
          <div>
            <dt className="text-micro uppercase text-ink-subtle">Company</dt>
            <dd className="mt-1 text-body text-ink">{client.company || '—'}</dd>
          </div>
          <div>
            <dt className="text-micro uppercase text-ink-subtle">Address</dt>
            <dd className="mt-1 whitespace-pre-line text-body text-ink">{client.address || '—'}</dd>
          </div>
        </dl>
        {client.notes && (
          <p className="mt-6 max-w-prose text-body text-ink-muted">{client.notes}</p>
        )}
      </section>

      <Tabs defaultValue="proposals" className="mt-10" data-testid="client-tabs">
        <TabsList>
          <TabsTrigger value="proposals">Proposals ({client.proposals?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({client.invoices?.length || 0})</TabsTrigger>
          <TabsTrigger value="projects">Projects ({client.projects?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className="mt-6">
          {!client.proposals?.length ? (
            <EmptyState title="No proposals yet" description="Proposals sent to this client will appear here." />
          ) : (
            <Table>
              <TableHeader className="hidden sm:table-header-group">
                <TableRow>
                  <TableHead>Proposal</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {client.proposals.map(p => (
                  <TableRow
                    key={p.id}
                    className={`${ROW} cursor-pointer`}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/proposals/${p.id}/edit`)}
                    onKeyDown={e => { if (e.key === 'Enter') navigate(`/proposals/${p.id}/edit`); }}
                  >
                    <TableCell data-label="Proposal" className={`${CELL} text-ink`}>{p.title}</TableCell>
                    <TableCell data-label="Created" className={`${CELL} text-ink-muted`}>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell data-label="Status" className={CELL}><StatusDot status={p.status} /></TableCell>
                    <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>{formatCurrency(p.total_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          {!client.invoices?.length ? (
            <EmptyState title="No invoices yet" description="Invoices billed to this client will appear here." />
          ) : (
            <Table>
              <TableHeader className="hidden sm:table-header-group">
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {client.invoices.map(inv => (
                  <TableRow key={inv.id} className={ROW}>
                    <TableCell data-label="Invoice" className={`${CELL} text-ink`}>#{inv.invoice_number}</TableCell>
                    <TableCell data-label="Due" className={`${CELL} text-ink-muted`}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell data-label="Status" className={CELL}><StatusDot status={inv.status} /></TableCell>
                    <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>{formatCurrency(inv.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="projects" className="mt-6">
          {!client.projects?.length ? (
            <EmptyState title="No projects yet" description="Projects are created automatically when a proposal is accepted." />
          ) : (
            <Table>
              <TableHeader className="hidden sm:table-header-group">
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {client.projects.map(proj => (
                  <TableRow
                    key={proj.id}
                    className={`${ROW} cursor-pointer`}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/projects/${proj.id}`)}
                    onKeyDown={e => { if (e.key === 'Enter') navigate(`/projects/${proj.id}`); }}
                  >
                    <TableCell data-label="Project" className={`${CELL} text-ink`}>{proj.name}</TableCell>
                    <TableCell data-label="Status" className={CELL}><StatusDot status={proj.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-md" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
            <DialogDescription>Update your client's details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name" htmlFor="edit-client-name" hint="Required">
              <Input id="edit-client-name" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Client name" data-testid="edit-client-name-input" />
            </Field>
            <Field label="Email" htmlFor="edit-client-email" error={emailError}>
              <Input
                id="edit-client-email"
                value={editForm.email}
                onChange={e => { setEditForm({ ...editForm, email: e.target.value }); setEmailError(''); }}
                placeholder="client@company.com"
                className={emailError ? 'border-destructive focus-visible:ring-destructive' : ''}
                data-testid="edit-client-email-input"
              />
            </Field>
            <Field label="Company" htmlFor="edit-client-company">
              <Input id="edit-client-company" value={editForm.company} onChange={e => setEditForm({ ...editForm, company: e.target.value })} placeholder="Company name" data-testid="edit-client-company-input" />
            </Field>
            <Field label="Phone" htmlFor="edit-client-phone">
              <Input id="edit-client-phone" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+1 (555) 000-0000" data-testid="edit-client-phone-input" />
            </Field>
            <Field label="Address" htmlFor="edit-client-address">
              <Textarea id="edit-client-address" value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} placeholder={'123 Main St, Suite 4\nAustin, TX 78701\nUSA'} data-testid="edit-client-address-input" />
            </Field>
            <Field label="Notes" htmlFor="edit-client-notes">
              <Textarea id="edit-client-notes" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Additional notes" data-testid="edit-client-notes-input" />
            </Field>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={saving} data-testid="update-client-btn">
                {saving ? 'Saving…' : 'Update client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-ink">{client.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-paper hover:bg-destructive/90"
              data-testid="confirm-delete-client-btn"
            >
              {deleting ? 'Deleting…' : 'Delete client'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
